import React, { useContext, useState } from "react";

import {
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
} from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  List,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import AddFoodPortionDialog from "@/components/FoodDatabase/AddFoodPortionDialog";
import useDialogState from "@/hooks/useDialogState";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { colors } from "@/theme/overrides/colors";
import { stopPropagation } from "@/utils/functions";

import OverwriteCellDialog from "./Dialogs/OverwriteCellDialog";
import PlannedMealItemsCellItem from "./PlannedMealItemsCellItem";
import { Cell } from "../store/Cell";
import MealPlanFormStore from "../store/MealPlanFormStore";
import { PlannedMealItem } from "../store/PlannedMealItem";

interface Props {
  className?: string;
  cell: Cell;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",
    padding: theme.spacing(2),
    cursor: "default",
    border: "2px solid #fafafa",
    backgroundColor: colors.white,
  },
  active: {
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0px 0px 40px rgba(17, 35, 64, 0.25)`,
  },
  iconButton: {
    marginLeft: theme.spacing(3),
    color: "#C0C5CD",
  },
  paste: {
    color: theme.palette.primary.main,
  },
  deleteActive: {
    color: "#FF6262",
  },
}));

const PlannedMealItemsCell: React.FC<Props> = observer(
  ({ className, cell }) => {
    const { classes, cx } = useStyles();
    const store = useContext(MealPlanFormStore.Context);
    const clipboard = store.clipboard;
    const canCopy = !clipboard.isCopying && !cell.isEmpty;
    const canPaste = clipboard.mode === "cell" && clipboard.copiedCell !== cell;
    const isDeleting = cell.isDeleting;
    const overwriteCellDialog = useDialogState();
    const [editItem, setEditItem] = useState<PlannedMealItem>();

    function preventRipple(e: React.MouseEvent) {
      e.stopPropagation();
    }

    function pasteClipboard() {
      if (!cell.isEmpty) {
        overwriteCellDialog.show();
      } else {
        confirmPasteClipboard();
      }
    }

    function confirmPasteClipboard() {
      clipboard.pasteCell(cell);
    }

    useKeyboardShortcut("Ctrl+C", () => {
      if (cell.isActive) clipboard.copyCell(cell);
    });

    useKeyboardShortcut("Ctrl+V", () => {
      if (cell.isActive) pasteClipboard();
    });

    useKeyboardShortcut("Ctrl+X", () => {
      if (cell.isActive) {
        clipboard.copyCell(cell);
        cell.clearItems();
      }
    });

    return (
      <>
        <ButtonBase
          component="div"
          className={cx(
            classes.root,
            className,
            cell.isActive && classes.active
          )}
          TouchRippleProps={{}}
          onClick={() => store.cells.setActive(cell)}
          data-cell={cell.key}
        >
          <List>
            {cell.items.map((item, index) => {
              const lastItem = index === cell.items.length - 1;
              return (
                <React.Fragment key={item.uuid}>
                  <PlannedMealItemsCellItem item={item} onEdit={setEditItem} />
                  {!lastItem && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
          <div>
            <Divider style={{ borderColor: colors.light }} />
            <Box
              width="100%"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingTop={2}
            >
              <Typography variant="subtitle1" color="initial">
                {Math.round(cell.totalEnergyKcal)} kcal
              </Typography>
              <Box display="flex" alignItems="center">
                {canCopy && (
                  <Tooltip title="Copy" placement="top">
                    <IconButton
                      className={classes.iconButton}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(() => clipboard.copyCell(cell))}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPaste && (
                  <Tooltip title="Paste" placement="top">
                    <IconButton
                      className={cx(classes.iconButton, classes.paste)}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(pasteClipboard)}
                    >
                      <AssignmentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {cell.items.length > 0 && (
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      className={cx(classes.iconButton, {
                        [classes.deleteActive]: isDeleting,
                      })}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(() => cell.toggleDeleting())}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </div>
        </ButtonBase>
        <OverwriteCellDialog
          cell={cell}
          open={overwriteCellDialog.shown}
          onClose={overwriteCellDialog.hide}
          onConfirm={confirmPasteClipboard}
        />
        <AddFoodPortionDialog
          open={!!editItem}
          foodId={editItem?.food.id ?? ""}
          defaultFoodPortionId={editItem?.foodPortion.id}
          defaultAmount={editItem?.amount}
          title="Edit Food Portions"
          onClose={() => setEditItem(undefined)}
          onAddFood={(_, foodPortion, amount) => {
            if (!editItem) return;
            editItem.setFoodPortion(foodPortion);
            editItem.setAmount(amount);
          }}
        />
      </>
    );
  }
);

export default PlannedMealItemsCell;
